// Use interfaces instead of types for better recursive type handling
// Interfaces are more forgiving with recursive structures in TypeScript
export interface FilterLineItem {
  type: 'line';
  id: string;
  operator?: FilterOperator;
  filterType?: FilterType;
  operatorType?: FilterOperatorType;
  value?: string;
}

export interface FilterGroupItem {
  type: 'group';
  id: string;
  operator?: FilterOperator;
  items: FilterItem[];
}

export type FilterItem = FilterLineItem | FilterGroupItem;

// Minimal interface for form operations we actually use
// This avoids the complex FormApi generic signature (12+ type parameters) while maintaining type safety
// By defining only what we use, we avoid TypeScript's recursive type inference issues
export interface FilterForm {
  getFieldValue: (name: string) => unknown;
  setFieldValue: (name: string, value: unknown) => void;
  Field: React.ComponentType<{
    name: string;
    defaultValue?: unknown;
    validators?: {
      onChange?: (value: unknown) => string | undefined;
      onBlur?: (value: unknown) => string | undefined;
    };
    children: (field: {
      state: { value: unknown; meta: { isTouched: boolean; isValid: boolean; errors: Array<{ message?: string }> } };
      handleChange: (value: unknown) => void;
      handleBlur: () => void;
    }) => React.ReactNode;
  }>;
  Subscribe: React.ComponentType<{
    selector: (state: { values: FilterGroupItem; isDirty: boolean; isValid: boolean }) => unknown;
    children: (value: unknown) => React.ReactNode;
  }>;
  store: { subscribe: (callback: () => void) => () => void };
  state: { values: FilterGroupItem; isDirty: boolean; isValid: boolean };
  handleSubmit: () => void;
  reset: (values?: FilterGroupItem, opts?: { keepDefaultValues?: boolean }) => void;
  update: (options: { defaultValues: FilterGroupItem }) => void;
}

export type FilterOperator = 'and' | 'or';

export type FilterValueConfig =
  | { type: 'text'; accessor?: string } // Default text value field, optional nested accessor
  | { type: 'select'; options: Array<{ value: string; label: string }>; accessor?: string } // ComboBox with fixed options, optional nested accessor
  | { type: 'date'; accessor?: string; config?: { allowNull?: boolean } } // Date picker, optional nested accessor
  | { type: 'nested'; accessor: string } // Nested field accessor (e.g., 'name' for assignee.name)
  | { type: 'boolean' }; // Auto-configures Yes/No dropdown with single 'eq' operator

export type FilterType = {
  id: string;
  label: string;
  filterValueConfig?: FilterValueConfig;
  filterOperatorTypeConfig?: FilterOperatorType[];
  /** Shows an `or` group of `eq` rules on this column as one pill listing the values. */
  combineOrGroups?: boolean;
};

/**
 * Filter operator type - uses SQL-like operator IDs
 */
export type FilterOperatorType = {
  id: string; // SQL-like operator: 'like', 'nlike', 'neq', 'eq', 'gt', 'gte', 'lt', 'lte', 'in', 'nin'
  label: string;
};

/**
 * Standard SQL-like filter operator types
 * These match the server-side filter format
 */
export const FILTER_OPERATOR_TYPES: FilterOperatorType[] = [
  { id: 'like', label: 'Includes' },
  { id: 'nlike', label: 'Does Not Include' },
  { id: 'eq', label: 'Equal' },
  { id: 'neq', label: 'Not Equal' },
  { id: 'gt', label: 'Greater Than' },
  { id: 'gte', label: 'Greater Than Or Equal' },
  { id: 'lt', label: 'Less Than' },
  { id: 'lte', label: 'Less Than Or Equal' },
  { id: 'in', label: 'In' },
  { id: 'nin', label: 'Not In' },
];

/**
 * Predefined operator type sets for common field types
 * These can be used in column meta to restrict available operators
 */
export const OPERATOR_TYPES = {
  /** Text field operators: includes, does not include, equals, not equals */
  TEXT: [
    { id: 'like', label: 'Includes' },
    { id: 'nlike', label: 'Does Not Include' },
    { id: 'eq', label: 'Equals' },
    { id: 'neq', label: 'Does Not Equal' },
  ] as FilterOperatorType[],

  /** Exact match operators: equals, not equals */
  EXACT: [
    { id: 'eq', label: 'Equals' },
    { id: 'neq', label: 'Does Not Equal' },
  ] as FilterOperatorType[],

  /** Date/number comparison operators */
  COMPARISON: [
    { id: 'eq', label: 'Equals' },
    { id: 'neq', label: 'Does Not Equal' },
    { id: 'gt', label: 'Greater Than' },
    { id: 'gte', label: 'Greater Than Or Equal' },
    { id: 'lt', label: 'Less Than' },
    { id: 'lte', label: 'Less Than Or Equal' },
  ] as FilterOperatorType[],

  /** Date-specific operators with readable labels */
  DATE: [
    { id: 'eq', label: 'On' },
    { id: 'gt', label: 'After' },
    { id: 'gte', label: 'On Or After' },
    { id: 'lt', label: 'Before' },
    { id: 'lte', label: 'On Or Before' },
  ] as FilterOperatorType[],

  /** List operators: in, not in */
  LIST: [
    { id: 'in', label: 'In' },
    { id: 'nin', label: 'Not In' },
  ] as FilterOperatorType[],

  /** Boolean operator: single 'eq' that gets auto-selected and hidden */
  BOOLEAN: [{ id: 'eq', label: 'Is' }] as FilterOperatorType[],
} as const;

/**
 * Operator types for checking null/empty values. Appended to a date filter's operator
 * options when its filterValueConfig.config.allowNull is true.
 */
export const NULL_OPERATOR_TYPES: FilterOperatorType[] = [
  { id: 'isNull', label: 'Is Empty' },
  { id: 'isNotEmpty', label: 'Is Not Empty' },
];

export const NULL_OPERATOR_IDS = new Set(NULL_OPERATOR_TYPES.map((ot) => ot.id));

/**
 * Resolves the operator options for a filter type, appending the null-check operators
 * for date filters that opt in via filterValueConfig.config.allowNull.
 */
export function getFilterOperatorTypes(filterType: FilterType): FilterOperatorType[] {
  const base = filterType.filterOperatorTypeConfig ?? OPERATOR_TYPES.TEXT;
  const valueConfig = filterType.filterValueConfig;
  if (valueConfig?.type === 'date' && valueConfig.config?.allowNull) {
    return [...base, ...NULL_OPERATOR_TYPES];
  }
  return base;
}

export type FilterShortcut = {
  label: string;
  getFilter: () => FilterGroupItem;
};

/**
 * A user-defined filter set, saved and applied exactly like a {@link FilterShortcut} —
 * it is a shortcut with a stable `id` (used for removal). `getFilter` is a function (not a
 * static group) for consistency with built-in shortcuts and so dynamic values (e.g. a
 * relative "today") can be computed at apply time when the backend rehydrates a saved set.
 * `id` is supplied by the consumer/backend (persistence is wired separately).
 */
export type SavedFilter = FilterShortcut & {
  id: string;
};

export const BOOLEAN_OPTIONS = [
  { value: 'true', label: 'True' },
  { value: 'false', label: 'False' },
];
